import { Payment } from '~models';
import { getField, updateField } from 'vuex-map-fields';
import { payments } from '~api';
import router from '~router';
import i18n from '../i18n';
import { Handler } from '~services';

const SET_CARDS = 'SET_CARDS';
const LOADING = 'LOADING';
const SET_PACKAGE_TYPE = 'SET_PACKAGE_TYPE';
const RESET_PAYMENT_DETAILS = 'RESET_PAYMENT_DETAILS';
const RESET_PACKAGE_TYPE = 'RESET_PACKAGE_TYPE';

const state = {
    cards: [],
    paymentDetails: { ...new Payment() },
    coupon: null,
    packageType: null,
    isLoading: false
};

const actions = {
    setPackageType({ commit, rootGetters }, val) {
        let packageType;
        if (val) {
            packageType = val;
        } else {
            const user = rootGetters['auth/user'];
            packageType =
                user &&
                user.subscription &&
                user.subscriptionStatus !== 'canceled'
                    ? user.subscription.stripePlan
                    : 'canceled';
        }

        window.localStorage.setItem('packageType', packageType);
        commit(SET_PACKAGE_TYPE, packageType);
    },

    async getCards({ commit }) {
        const resp = await payments.getCards();

        if (resp.ok) {
            return commit(SET_CARDS, resp.data);
        }

        return Handler.handleResponse(resp);
    },

    resetCard({ commit }) {
        commit(RESET_PAYMENT_DETAILS);
        router.go(-1);
    },

    /**
     * Add new card
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<*>}
     */
    async addNewCard({ commit, state }) {
        const successMessage = i18n.t('payments.successMessages.cardUpdated');

        commit(LOADING);

        const resp = await payments.addNewCard(state.paymentDetails);

        if (resp.ok) {
            commit(RESET_PAYMENT_DETAILS);
        }

        commit(LOADING);

        return Handler.handleResponse(
            resp,
            successMessage,
            undefined,
            'dashboard.profile'
        );
    },

    /**
     * Delete card
     * @param {function} commit
     * @param {function} dispatch
     * @returns {Promise<boolean>}
     */
    async deleteCard({ commit, dispatch, state }) {
        const redirectName = 'dashboard.profile';
        const successMessage = i18n.t('payments.successMessages.cardDeleted');

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.deleteCard'),
            { root: true }
        );
        if (!confirmed) {
            return;
        }

        commit(LOADING);

        if (state.cards && state.cards.length) {
            // Api request on delete account
            const resp = await payments.deleteCard(state.cards[0].id);

            // Handle response
            Handler.handleResponse(resp, successMessage, '', redirectName);
        }

        commit(LOADING);
    },

    /**
     * Create subscription
     * @param {function} commit
     * @param {object} state
     * @param {function} dispatch
     * @returns {Promise<*>}
     */
    async createSubscription({ commit, state, dispatch, rootGetters }) {
        // Check current subscription before create new
        await dispatch('auth/getProfile', null, { root: true });

        if (rootGetters['auth/notAllowToSubscribe']) {
            return Handler.handleResponse(
                null,
                null,
                'You are already subscribed. Please contact support if you have any questions',
                'dashboard.themes'
            );
        }

        const successMessage = i18n.t(
            'payments.successMessages.subscriptionCreated'
        );
        const data = {
            slug: state.packageType,
            card: state.paymentDetails
        };

        if (state.coupon) data.coupon = state.coupon;

        commit(LOADING);

        const resp = await payments.createSubscription(data);

        if (resp.ok) {
            await dispatch('auth/getProfile', null, { root: true });
            commit(RESET_PAYMENT_DETAILS);
        }

        commit(LOADING);

        return Handler.handleResponse(
            resp,
            successMessage,
            undefined,
            'dashboard.themes'
        );
    },
    async updateSubscription({ commit, state, dispatch }) {
        const successMessage = i18n.t(
            'payments.successMessages.subscriptionUpdated'
        );
        commit(LOADING);

        const resp = await payments.updateSubscription({
            slug: state.packageType
        });

        if (resp.ok) {
            await dispatch('auth/getProfile', null, { root: true });
            commit(RESET_PACKAGE_TYPE);
        }

        commit(LOADING);

        return Handler.handleResponse(resp, successMessage, undefined);
    },
    async deleteSubscription({ commit, dispatch }) {
        const successMessage = i18n.t(
            'payments.successMessages.subscriptionCanceled'
        );

        // Show confirm modal window
        const confirmed = await dispatch(
            'prompt/show',
            i18n.t('prompt.cancelSubscription'),
            { root: true }
        );
        if (!confirmed) return;

        commit(LOADING);

        // Api request on delete account
        const resp = await payments.deleteSubscription();

        if (resp.ok) {
            await dispatch('auth/getProfile', null, { root: true });
        }

        // Handle response
        Handler.handleResponse(resp, successMessage);

        commit(LOADING);

        return resp.ok;
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex state instance.
    updateField,

    [LOADING](state) {
        state.isLoading = !state.isLoading;
    },
    [SET_PACKAGE_TYPE](state, val) {
        state.packageType = val;
    },
    [SET_CARDS](commit, data) {
        state.cards = data;
    },
    [RESET_PAYMENT_DETAILS](state) {
        state.paymentDetails = { ...new Payment() };
    },
    [RESET_PACKAGE_TYPE](state) {
        const { subscription } = JSON.parse(
            window.localStorage.getItem('user')
        );
        state.paymentDetails = subscription.stripePlan
            ? subscription.stripePlan
            : null;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex state instance.
    getField,
    isLoading: state => state.isLoading,
    packageType: state => {
        if (state.packageType) return state.packageType;

        const packageType = window.localStorage.getItem('packageType');

        return packageType;
    },
    lastDigits: state => {
        if (state.cards && state.cards.length) {
            return `**** **** **** ${state.cards[0].card.last4}`;
        }
        return null;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
